/**
 * Multi-select state for list items with haptic feedback.
 */
import { useState, useCallback, useMemo } from 'react';
import { haptic } from '@/utils/haptics';

interface HasId {
  id: string;
}

export function useSelectionSet<T extends HasId>(items: T[]) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds]
  );

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    haptic.light();
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === items.length) return new Set();
      return new Set(items.map((item) => item.id));
    });
    haptic.light();
  }, [items]);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
    haptic.light();
  }, []);

  const count = selectedIds.size;
  const allSelected = items.length > 0 && selectedIds.size === items.length;

  return useMemo(
    () => ({ selectedIds, isSelected, toggle, selectAll, clear, count, allSelected }),
    [selectedIds, isSelected, toggle, selectAll, clear, count, allSelected]
  );
}
